import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PageLayout from '@/components/layout/PageLayout.tsx';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton.tsx';
import { IconScout } from '@/components/ui/images/IconScout.tsx';
import Table from '@/components/ui/tables/Table.tsx';
import { Selector } from '@/components/ui/inputs/selector';
import { RadioButtonGroup } from '@/components/ui/inputs/RadioButtonGroup.tsx';
import { DatePicker } from '@/components/ui/filters/DatePicker.tsx';
import { ExportDropdown } from '@/components/ui/filters/ExportDropdown.tsx';
import type { UIExportFormat } from '@/types/fileExport.types';
import { useMonthlyAttendance } from '@/hooks/attendance/monthlyCuts/useMonthlyAttendance.ts';
import { DynamicFiltersCard } from '@/components/ui/cards/DynamicFiltersCard.tsx';
import { getRangeTypeOptions, getFormatTypeOptions } from '@/types/attendanceFilter.types.ts';
import type { FilterField } from '@/components/ui/cards/DynamicFiltersCard.tsx';
import type { MonthlyAttendanceFilters } from '@/types/attendanceFilter.types.ts';
import type { TFunction } from 'i18next';


interface FilterFieldsParams {
  translate: TFunction;
  filters: MonthlyAttendanceFilters;
  setFilters: React.Dispatch<React.SetStateAction<MonthlyAttendanceFilters>>;
  cutoffOptions: Array<{ value: string; label: string; supportingText?: string }>;
  rangeTypeOptions: Array<{ value: string; label: string }>;
  formatTypeOptions: Array<{ value: string; label: string }>;
  loadingCutoffs: boolean;
  maxDate: Date;
  minDate: Date;
  handleRangeTypeChange: (value: any) => void;
  handleSearch: () => void;
  canSearch: boolean;
  isLoading: boolean;
  handleExport: (format: UIExportFormat) => void;
  hasData: boolean;
  years: Array<{ value: string; label: string }>;
  months: Array<{ value: string; label: string }>;
}

const buildFilterFields = (params: FilterFieldsParams): FilterField[] => {
  const {
    translate,
    filters,
    setFilters,
    cutoffOptions,
    rangeTypeOptions,
    formatTypeOptions,
    loadingCutoffs,
    maxDate,
    minDate,
    handleRangeTypeChange,
    handleSearch,
    canSearch,
    isLoading,
    handleExport,
    hasData,
    years,
    months,
  } = params;

  return [
    // MONTHLY mode - Row 1: año | mes | descripcion | formato horas
    {
      id: 'yearSelector',
      type: 'custom',
      colSpan: 1,
      hidden: filters.searchMode !== 'MONTHLY',
      loading: loadingCutoffs,
      component: (
        <Selector
          label={translate('attendance:monthly.filters.year')}
          value={filters.year ? String(filters.year) : ''}
          onChange={(value) => setFilters(previousFilters => ({ 
            ...previousFilters, 
            year: Number(value),
            month: undefined,
            cutoffId: undefined,
          }))}
          options={years}
          placeholder={translate('attendance:monthly.filters.yearPlaceholder')}
        />
      ),
    },
    {
      id: 'monthSelector',
      type: 'custom',
      colSpan: 1,
      hidden: filters.searchMode !== 'MONTHLY',
      loading: loadingCutoffs,
      component: (
        <Selector
          label={translate('attendance:monthly.filters.month')}
          value={filters.month ? String(filters.month) : ''}
          onChange={(value) => setFilters(previousFilters => ({ 
            ...previousFilters, 
            month: Number(value),
            cutoffId: undefined,
          }))}
          options={months}
          disabled={!filters.year}
          placeholder={translate('attendance:monthly.filters.monthPlaceholder')}
        />
      ),
    },
    {
      id: 'monthlySelector',
      type: 'custom',
      colSpan: 1,
      hidden: filters.searchMode !== 'MONTHLY',
      loading: loadingCutoffs,
      component: (
        <Selector
          label={translate('attendance:monthly.filters.cutoff')}
          value={filters.cutoffId ? String(filters.cutoffId) : ''}
          onChange={(value) => setFilters(previousFilters => ({ 
            ...previousFilters, 
            cutoffId: Number(value), 
          }))}
          options={cutoffOptions}
          disabled={!filters.year || !filters.month || cutoffOptions.length === 0}
          placeholder={translate('attendance:monthly.filters.cutoffPlaceholder')}
          required
        />
      ),
    },
    // RANGE mode - Row 1: Fecha desde | Fecha hasta | Tipo de rango | formato horas
    {
      id: 'dateFrom',
      type: 'dateRange',
      colSpan: 1,
      hidden: filters.searchMode !== 'RANGE',
      component: (
        <DatePicker
          label={translate('attendance:monthly.filters.dateFrom')}
          value={filters.dateFrom || ''}
          onChange={(date) => setFilters(previousFilters => ({
            ...previousFilters,
            dateFrom: date,
          }))}
          maxDate={maxDate.toISOString().split('T')[0]}
          minDate={minDate.toISOString().split('T')[0]}
        />
      ),
    },
    {
      id: 'dateTo',
      type: 'dateRange',
      colSpan: 1,
      hidden: filters.searchMode !== 'RANGE',
      component: (
        <DatePicker
          label={translate('attendance:monthly.filters.dateTo')}
          value={filters.dateTo || ''}
          onChange={(date) => setFilters(previousFilters => ({
            ...previousFilters,
            dateTo: date,
          }))}
          maxDate={maxDate.toISOString().split('T')[0]}
          minDate={minDate.toISOString().split('T')[0]}
        />
      ),
    },
    {
      id: 'rangeType',
      type: 'selector',
      colSpan: 1,
      hidden: filters.searchMode !== 'RANGE',
      component: (
        <Selector
          label={translate('attendance:monthly.filters.rangeType')}
          value={filters.rangeType || ''}
          onChange={(value) => handleRangeTypeChange(value as any)}
          options={rangeTypeOptions}
          placeholder="Seleccione"
        />
      ),
    },
    // Row 1 - Empty space
    {
      id: 'emptySpace1',
      type: 'custom',
      colSpan: 1,
      component: <div />,
    },
    // Row 2: formato horas | buscar | disponible | descargar
    {
      id: 'formatType',
      type: 'selector',
      colSpan: 1,
      component: (
        <Selector
          label={translate('attendance:monthly.filters.formatType')}
          value={filters.formatType}
          onChange={(value) => setFilters(previousFilters => ({ 
            ...previousFilters, 
            formatType: value as 'HH:MM:SS' | 'DECIMAL', 
          }))}
          options={formatTypeOptions}
          required
        />
      ),
    },
    {
      id: 'searchButton',
      type: 'custom',
      colSpan: 1,
      component: (
        <div className="flex items-center justify-start h-full">
          <PrimaryButton
            onClick={handleSearch}
            disabled={!canSearch}
            isLoading={isLoading}
            iconLeft={<IconScout name="search" />}
            data-testid="primary-button"
          >
            {translate('attendance:monthly.actions.search')}
          </PrimaryButton>
        </div>
      ),
    },
    {
      id: 'emptySpace2',
      type: 'custom',
      colSpan: 1,
      component: <div />,
    },
    {
      id: 'downloadButton',
      type: 'custom',
      colSpan: 1,
      component: (
        <div className="flex items-end justify-end h-full">
          <ExportDropdown
            onExport={handleExport}
            disabled={!hasData}
            isLoading={isLoading}
            placement="right"
          />
        </div>
      ),
    },
  ];
};

export const MyMonthlyAttendance: React.FC = () => {
  const { t: translate } = useTranslation();
  const {
    filters,
    setFilters,
    rows,
    totalRecords,
    page,
    pageSize,
    isLoading,
    cutoffs,
    loadingCutoffs,
    canSearch,
    handleSearch,
    setPage,
    setPageSize,
    onSearch,
    getValue,
    handleRangeTypeChange,
    handleExport,
    columns,
    actions,
    years,
    months,
  } = useMonthlyAttendance();

  const handleSearchModeChange = (value: string) => {
    const newMode = value as 'MONTHLY' | 'RANGE';
    const today = new Date();
    const todayFormatted = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    
    if (newMode === 'RANGE') {
      setFilters(previousFilters => ({
        ...previousFilters,
        searchMode: newMode,
        dateFrom: todayFormatted,
        dateTo: todayFormatted,
        rangeType: 'TODAY',
      }));
    } else {
      setFilters(previousFilters => ({
        ...previousFilters,
        searchMode: newMode,
      }));
    }
  };

  const rangeTypeOptions = useMemo(() => getRangeTypeOptions(translate), [translate]);
  const formatTypeOptions = useMemo(() => getFormatTypeOptions(translate), [translate]);

  // Si cutoffs ya viene agrupado, lo pasamos directo
  const cutoffOptions = useMemo(() => {
    // Si el primer elemento tiene 'options', es agrupado
    if (cutoffs.length > 0 && (cutoffs[0] as any).options) {
      return cutoffs as any;
    }
    // Si no, lo mapeamos plano
    return cutoffs.map(cutoff => ({
      value: String((cutoff as any).value),
      label: (cutoff as any).label,
      supportingText: (cutoff as any).supportingText,
    }));
  }, [cutoffs]);

  const maxDate = useMemo(() => new Date(), []);
  const minDate = useMemo(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 5);
    return date;
  }, []);

  const hasData = useMemo(() => rows.length > 0, [rows]);

  const filterFields = useMemo(() => buildFilterFields({
    translate,
    filters,
    setFilters,
    cutoffOptions,
    rangeTypeOptions,
    formatTypeOptions,
    loadingCutoffs,
    maxDate,
    minDate,
    handleRangeTypeChange,
    handleSearch,
    canSearch,
    isLoading,
    handleExport,
    hasData,
    years,
    months,
  }), [
    translate,
    filters,
    setFilters,
    cutoffOptions,
    rangeTypeOptions,
    formatTypeOptions,
    loadingCutoffs,
    maxDate,
    minDate,
    handleRangeTypeChange,
    handleSearch,
    canSearch,
    isLoading,
    handleExport,
    hasData,
    years,
    months,
  ]);

  return (
    <PageLayout>
      <div className="flex flex-col gap-4" data-testid="monthly-attendance-page">
        <DynamicFiltersCard
          header={
            <RadioButtonGroup
              name="searchMode"
              options={[
                { value: 'MONTHLY', label: translate('attendance:monthly.filters.monthly') },
                { value: 'RANGE', label: translate('attendance:monthly.filters.range') },
              ]}
              value={filters.searchMode}
              onChange={handleSearchModeChange}
              direction="horizontal"
            />
          }
          fields={filterFields}
        />

        <Table
          columns={columns}
          data={rows}
          totalRows={totalRecords}
          isLoading={isLoading}
          searchable={true}
          onSearch={onSearch}
          pagination={{
            page: page,
            pageSize: pageSize,
            total: totalRecords,
            onChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          data-testid="table-monthly-attendance"
          actions={actions}
          expandable={{
            showExpandColumn: true,
            rowExpandable: (record) =>
              !!record.comments || (record.marks && record.marks.length > 0),
            getExpandedFields: (record) => {
              const fields = [
                {
                  label: translate('attendance:monthly.table.marks'),
                  value: record.marks.join(', '),
                },
                {
                  label: translate('attendance:monthly.table.overtime'),
                  value: getValue(record, 'overtime'),
                },
              ];

              const schedule = record.raw.theoreticalSchedule;
              if (schedule && schedule.entryTime && schedule.exitTime) {
                let scheduleText = `${schedule.entryTime} - ${schedule.exitTime}`;
                if (schedule.lunchStartTime && schedule.lunchEndTime) {
                  scheduleText = `${schedule.entryTime} - ${schedule.lunchStartTime} | ${schedule.lunchEndTime} - ${schedule.exitTime}`;
                }
                fields.push({
                  label: translate('attendance:monthly.table.schedule'),
                  value: scheduleText,
                });
              }

              fields.push(
                {
                  label: translate('attendance:monthly.table.comments'),
                  value: record.comments,
                },
              );
              return fields;
            },
          }}
        />
      </div>
    </PageLayout>
  );
};

export default MyMonthlyAttendance;
